import { composeDashboardReadModel, type DashboardReadModel, type DashboardReadSourceFamily } from "../domain/dashboard-read-model";
import type { CoachingRole } from "../domain/roles";
import { ApiClientError } from "./api-client";
import { isLiveDataMode } from "../lib/config";
import type {
  CoachingArtifactDto,
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingHistoryEventDto,
  CoachingResourceDto,
  CoachingSessionDto,
  ListResponse
} from "./coaching-dtos";
import type { CoachingRequester } from "./coaching-services";
import type { CoachingEndpointKey } from "./coaching-contracts";

type Requester = CoachingRequester;
type Query = Record<string, string | number | boolean | null | undefined>;

interface FamilyRead<T> {
  items: T[];
  total: number | undefined;
}

type ListRequester = <T>(requester: Requester, key: CoachingEndpointKey, query?: Query) => Promise<ListResponse<T>>;

export async function composedDashboardReadModel(
  requester: Requester,
  list: ListRequester,
  role: CoachingRole
): Promise<DashboardReadModel> {
  const query = { role };
  const deniedFamilies: DashboardReadSourceFamily[] = [];
  let deniedReference: string | undefined;

  // The live backend currently returns 403 for some participant-scoped list
  // reads. Those families compose as explicitly-denied empty reads (recorded
  // in deniedFamilies) instead of failing the whole dashboard or widening
  // permissions client-side; every other failure still rejects honestly.
  async function familyRead<T>(key: CoachingEndpointKey, family: DashboardReadSourceFamily): Promise<FamilyRead<T>> {
    try {
      const response = await list<T>(requester, key, query);
      return {
        items: response.items,
        total: response.total ?? (isLiveDataMode() ? undefined : response.items.length)
      };
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 403) {
        deniedFamilies.push(family);
        deniedReference ??= error.reference ?? undefined;
        return { items: [], total: undefined };
      }
      throw error;
    }
  }

  const companyRead =
    role === "cohort_lead"
      ? Promise.resolve({ items: [] as CoachingCompanyDto[], total: 0 })
      : familyRead<CoachingCompanyDto>("companies.list", "company-service");
  const [companyResult, cohortResult, sessionResult, reportResult, resourceResult, historyResult] = await Promise.all([
    companyRead,
    familyRead<CoachingCohortDto>("cohorts.list", "cohort-service"),
    familyRead<CoachingSessionDto>("sessions.list", "session-service"),
    familyRead<CoachingArtifactDto>("reports.list", "artifact-service"),
    familyRead<CoachingResourceDto>("resources.list", "resource-service"),
    familyRead<CoachingHistoryEventDto>("history.list", "history-service")
  ]);

  const { items: companies, total: companiesTotal } = companyResult;
  const { items: cohorts, total: cohortsTotal } = cohortResult;
  const { items: sessions, total: sessionsTotal } = sessionResult;
  const { items: reports, total: reportsTotal } = reportResult;
  const { items: resources, total: resourcesTotal } = resourceResult;
  const { items: history, total: historyTotal } = historyResult;

  return composeDashboardReadModel({
    role,
    companies,
    cohorts,
    sessions,
    reports,
    resources,
    history,
    totals: {
      companies: companiesTotal,
      cohorts: cohortsTotal,
      sessions: sessionsTotal,
      reports: reportsTotal,
      resources: resourcesTotal,
      history: historyTotal
    },
    deniedFamilies,
    supportRef: deniedReference
  });
}
